/**
 * `PlanUsageBanner` — ADR-0263 §7.2 (S8) over-the-limit banner.
 *
 * Reads `/v1/plan-usage` (through `getPlanUsage`) and renders a
 * dimension-specific banner for the given site:
 *
 *   - Orders near (state `near` on `orders_monthly`): amber.
 *   - Orders exceeded: coral. Orders past allowed are ALWAYS
 *     evaluated by the merchant's policy — the banner tells the
 *     merchant to upgrade but no rail behaviour changes (CI security
 *     review 2026-11-04 removed the edge skip-rules path).
 *   - Page loads exceeded: coral, says "Visit counting paused for
 *     {domain}. Blocking and security alerts are still on. Charts
 *     resume on {1 Month}."
 *   - Assistant exceeded: coral, says "Assistant paused until {1
 *     Month}. Claude and ChatGPT get an upgrade message instead of
 *     answers."
 *
 * When `enforcement_live` is `false` on the response, the banner
 * swaps the pause sentence for "Nothing is blocked." (the §7.1 rule:
 * the row is data; the master switch gates behaviour). Orders copy
 * is the same whether the flag is on or off — orders behaviour never
 * changes on the rail.
 *
 * Returns `null` when nothing qualifies. The three mount sites
 * (Overview, Activity, Account → Billing) render it per site as a
 * leading tile; it stays visual-only in S8, same posture as S3.
 */

import { useEffect, useState } from 'react';

import { useOptionalHttp } from '../../api/http-context.js';
import {
  getPlanUsage,
  type PlanUsageMeter,
  type PlanUsageResponse,
  type PlanUsageSite,
} from '../../api/plan-usage.js';
import { useOptionalAuth } from '../../app/auth-context.js';
import { useC } from '../../lib/theme.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

type LoadState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly data: PlanUsageResponse }
  | { readonly kind: 'error' };

export interface PlanUsageBannerProps {
  readonly site_id: string;
  readonly domain?: string | null;
  /** When true (default) the banner may show for `state === 'near'`
   *  (orders only); false hides near-state. */
  readonly include_near?: boolean;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

/** 1-based month index from an ISO date string (period_end for the
 *  "resumes on 1 <month>" line). */
function nextMonthLabel(period_end_iso: string): string {
  const d = new Date(period_end_iso);
  if (Number.isNaN(d.getTime())) return '';
  return `1 ${MONTH_NAMES[d.getUTCMonth()]!}`;
}

/** Pick the one banner this site should show, in coral > amber order. */
export function pickBanner(
  site: PlanUsageSite | undefined,
  account_assistant: PlanUsageMeter | null,
  include_near: boolean,
): {
  readonly tone: 'amber' | 'coral';
  readonly dimension: 'orders_monthly' | 'page_loads_monthly' | 'assistant_requests_monthly';
} | null {
  if (site === undefined) {
    if (account_assistant !== null && account_assistant.state === 'exceeded') {
      return { tone: 'coral', dimension: 'assistant_requests_monthly' };
    }
    return null;
  }
  if (site.page_loads.state === 'exceeded') {
    return { tone: 'coral', dimension: 'page_loads_monthly' };
  }
  if (site.orders.state === 'exceeded') {
    return { tone: 'coral', dimension: 'orders_monthly' };
  }
  if (include_near && site.orders.state === 'near') {
    return { tone: 'amber', dimension: 'orders_monthly' };
  }
  if (account_assistant !== null && account_assistant.state === 'exceeded') {
    return { tone: 'coral', dimension: 'assistant_requests_monthly' };
  }
  return null;
}

export function renderBannerCopy(args: {
  readonly dimension: 'orders_monthly' | 'page_loads_monthly' | 'assistant_requests_monthly';
  readonly tone: 'amber' | 'coral';
  readonly enforcement_live: boolean;
  readonly usage: number;
  readonly allowed: number;
  readonly limit: number;
  readonly domain: string;
  readonly period_end_iso: string;
}): string {
  const next = nextMonthLabel(args.period_end_iso);
  // Orders behaviour never changes on the rail — the enforcement_live
  // flag gates the page-loads + assistant copy only. Orders always
  // keep being checked.
  if (args.dimension === 'orders_monthly') {
    if (args.tone === 'amber') {
      return `Orders: ${args.usage} of ${args.limit} this month. Over the limit, orders keep going through and are still checked.`;
    }
    return `Orders past the plan's allowance this month. They keep going through and are still checked. Upgrade to stay within your plan.`;
  }
  if (!args.enforcement_live) return 'Nothing is blocked.';
  if (args.dimension === 'page_loads_monthly') {
    return `Visit counting paused for ${args.domain}. Blocking and security alerts are still on. Charts resume on ${next}.`;
  }
  // assistant_requests_monthly
  return `Assistant paused until ${next}. Claude and ChatGPT get an upgrade message instead of answers.`;
}

/**
 * ADR-0263 §7.2 (S8) — Billing-tab variant. Iterates the account's
 * sites and renders one banner per site that has a near / exceeded
 * state (and optionally one account-level assistant banner, attached
 * to the site that anchors the pause row). Owner/admin only;
 * role-gated inside each child.
 */
export function PlanUsageBannersList(): JSX.Element | null {
  const http = useOptionalHttp();
  const auth = useOptionalAuth();
  const role = auth?.state.kind === 'authenticated' ? auth.state.user.role : undefined;
  // Security review 2026-11-04 Minor 5 — unknown role = cannot see.
  const canSee = role === 'owner' || role === 'admin';
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  useEffect(() => {
    if (http === null || !canSee) return;
    let cancelled = false;
    void getPlanUsage(http).then((res) => {
      if (cancelled) return;
      if (res.kind === 'ok') setState({ kind: 'ready', data: res.data });
      else setState({ kind: 'error' });
    });
    return () => {
      cancelled = true;
    };
  }, [http, canSee]);
  if (!canSee) return null;
  if (state.kind !== 'ready') return null;
  const data = state.data;
  // Defensive: parents mount this during route transitions; ensure a
  // null / wrong-shape response never crashes the tree.
  if (data === null || typeof data !== 'object') return null;
  if (!data.active) return null;
  if (!Array.isArray(data.sites)) return null;
  // Render a banner for each site whose own picks is non-null; at
  // most one banner per site. The account-level assistant banner is
  // surfaced once (on the account's first qualifying site) because
  // `pickBanner` prefers the site's own state.
  const rendered: JSX.Element[] = [];
  let account_assistant_rendered = false;
  for (const s of data.sites) {
    const own = pickBanner(s, null, true);
    if (own !== null) {
      rendered.push(
        <PlanUsageBanner key={s.site_id} site_id={s.site_id} domain={null} />,
      );
      continue;
    }
    // Site has no site-level banner, but the account assistant may be
    // exceeded. Attach it to the first site we see so there's one
    // visible place to click Upgrade.
    if (
      !account_assistant_rendered
      && data.account.assistant_requests !== null
      && data.account.assistant_requests.state === 'exceeded'
    ) {
      rendered.push(
        <PlanUsageBanner key={`${s.site_id}:assistant`} site_id={s.site_id} domain={null} />,
      );
      account_assistant_rendered = true;
    }
  }
  if (rendered.length === 0) return null;
  return <div className="space-y-2">{rendered}</div>;
}

export function PlanUsageBanner(props: PlanUsageBannerProps): JSX.Element | null {
  const http = useOptionalHttp();
  // ADR-0263 §7.2 — same floor as PlanAndUsageSection: plan + usage
  // data are billing data; reviewers / viewers do not see them. A
  // missing / unknown role is treated as cannot-see (security
  // review 2026-11-04 Minor 5) — the banner never leaks plan state
  // to a session whose role we cannot confirm.
  const auth = useOptionalAuth();
  const role = auth?.state.kind === 'authenticated' ? auth.state.user.role : undefined;
  const canSee = role === 'owner' || role === 'admin';
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  useEffect(() => {
    if (http === null || !canSee) return;
    let cancelled = false;
    void getPlanUsage(http).then((res) => {
      if (cancelled) return;
      if (res.kind === 'ok') setState({ kind: 'ready', data: res.data });
      else setState({ kind: 'error' });
    });
    return () => {
      cancelled = true;
    };
  }, [http, canSee]);
  const c = useC();
  if (!canSee) return null;
  if (state.kind !== 'ready') return null;
  const data = state.data;
  // Defensive: a bad API response shape (null, missing `sites`) must
  // not crash the parent tree. Treat as "nothing to show".
  if (data === null || typeof data !== 'object') return null;
  if (!data.active) return null;
  if (!Array.isArray(data.sites)) return null;
  const site = data.sites.find((s) => s.site_id === props.site_id);
  const pick = pickBanner(
    site,
    data.account?.assistant_requests ?? null,
    props.include_near ?? true,
  );
  if (pick === null) return null;
  // Extract meter / allowed / limit for copy.
  let usage = 0;
  let allowed = 0;
  let limit = 0;
  if (pick.dimension === 'assistant_requests_monthly') {
    const r = data.account.assistant_requests;
    if (r !== null) {
      usage = r.usage;
      allowed = r.allowed ?? r.limit ?? 0;
      limit = r.limit ?? 0;
    }
  } else if (site !== undefined) {
    const r = pick.dimension === 'page_loads_monthly' ? site.page_loads : site.orders;
    usage = r.usage;
    allowed = r.allowed ?? r.limit ?? 0;
    limit = r.limit ?? 0;
  }
  const copy = renderBannerCopy({
    dimension: pick.dimension,
    tone: pick.tone,
    enforcement_live: data.enforcement_live,
    usage,
    allowed,
    limit,
    domain: props.domain ?? props.site_id,
    period_end_iso: data.period_end,
  });
  const bg = pick.tone === 'coral' ? c.coralDim : c.amberDim;
  const border = pick.tone === 'coral' ? c.coral : c.amber;
  const fg = pick.tone === 'coral' ? c.coralFg : c.amberFg;
  // ADR-0263 §7.2 — CTA buttons. "Upgrade site" always links to the
  // existing v2 plan picker (BillingPanelV2 renders at `#billing`);
  // the pack button renders when a dimension has a known pack kind
  // (page_loads / orders). Both scroll to the same section so the
  // merchant lands on the self-serve flow.
  const upgrade_href = `?view=account&account_tab=billing#billing`;
  const pack_supported =
    pick.dimension === 'page_loads_monthly' || pick.dimension === 'orders_monthly';
  return (
    <div
      data-testid={`plan-usage-banner-${pick.dimension}`}
      data-tone={pick.tone}
      data-enforcement-live={data.enforcement_live ? 'true' : 'false'}
      style={{
        padding: '10px 12px',
        borderRadius: 3,
        border: `1px solid ${border}`,
        background: bg,
        color: c.ink,
        fontFamily: sansFont,
        fontSize: 13,
        lineHeight: 1.45,
      }}
    >
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          letterSpacing: 1.5,
          textTransform: 'uppercase',
          marginBottom: 4,
          color: fg,
        }}
      >
        {pick.tone === 'coral' ? 'Attention' : 'Heads up'}
      </div>
      <div style={{ marginBottom: 6 }}>{copy}</div>
      <div className="flex items-center gap-2 flex-wrap">
        <a
          href={upgrade_href}
          data-testid="plan-usage-banner-upgrade"
          style={{
            display: 'inline-block',
            padding: '4px 10px',
            fontFamily: sansFont,
            fontSize: 12,
            fontWeight: 600,
            color: c.ink,
            background: c.lime,
            borderRadius: 2,
            textDecoration: 'none',
          }}
        >
          Upgrade site
        </a>
        {pack_supported && (
          <a
            href={upgrade_href}
            data-testid="plan-usage-banner-pack"
            style={{
              display: 'inline-block',
              padding: '4px 10px',
              fontFamily: sansFont,
              fontSize: 12,
              fontWeight: 500,
              color: c.text,
              background: 'transparent',
              border: `1px solid ${c.border}`,
              borderRadius: 2,
              textDecoration: 'none',
            }}
          >
            Add a pack
          </a>
        )}
      </div>
    </div>
  );
}
